import type { NMPLocale } from "@borealis/locale-common";
import { formatNumber } from "@borealis/number-format";
import { i18n } from "@lingui/core";
import { RangeSlider } from "@warp-ds/components/react/slider";
import React from "react";
import { SemanticFilter } from "src/types/SemanticFilter";
import { RangeForm } from "./RangeForm";

export type RangeFilterProps = {
	filter: SemanticFilter;
	index: number;
	selected?: unknown;
	onChange?: (next: unknown) => void;
	formatValue?: (value: number) => string;
};

export function RangeFilter({ filter, index, selected, onChange, formatValue }: RangeFilterProps) {
	const rawMin = Number(filter.min_value ?? 0);
	const rawMax = Number(filter.max_value ?? 100);
	const rawStep = Number(filter.step ?? 1);

	// Use safe defaults if filter config is invalid
	const min = Number.isFinite(rawMin) ? rawMin : 0;
	const max = Number.isFinite(rawMax) ? rawMax : 100;
	const step = Number.isFinite(rawStep) ? rawStep : 1;
	const initialFrom = min;
	const initialTo = max;

	const parseSelected = (input: unknown): [number | undefined, number | undefined] => {
		if (!Array.isArray(input) || input.length !== 2) return [undefined, undefined];
		const from = Number(input[0]);
		const to = Number(input[1]);
		return [Number.isFinite(from) ? from : undefined, Number.isFinite(to) ? to : undefined];
	};

	const [userMin, setUserMin] = React.useState<number | undefined>(() => parseSelected(selected)[0]);
	const [userMax, setUserMax] = React.useState<number | undefined>(() => parseSelected(selected)[1]);
	const [range, setRange] = React.useState<[number, number]>([
		parseSelected(selected)[0] ?? initialFrom,
		parseSelected(selected)[1] ?? initialTo,
	]);

	React.useEffect(() => {
		const [parsedMin, parsedMax] = parseSelected(selected);
		setUserMin(parsedMin);
		setUserMax(parsedMax);
		setRange([parsedMin ?? initialFrom, parsedMax ?? initialTo]);
	}, [initialFrom, initialTo, filter.name, selected]);

	const deriveNextUserSelection = (next: [number, number]) => {
		const [nextMin, nextMax] = next;
		const updatedUserMin = userMin === undefined && nextMin === initialFrom ? undefined : nextMin;
		const updatedUserMax = userMax === undefined && nextMax === initialTo ? undefined : nextMax;
		return { updatedUserMin, updatedUserMax };
	};

	const filterName = filter.display_name ?? filter.name ?? i18n.t("explore.filter.fallback", { message: "Filter" });
	const labelText =
		filter.name === "price"
			? i18n.t({ id: "explore.filter.price.label", message: "Price range" })
			: i18n.t("explore.filter.range.label", { filterName }, { message: "{filterName} range" });
	const locale = i18n.locale as NMPLocale;

	// Format for labels (with currency if provided)
	const formatRangeValue = React.useCallback(
		(value: number) => {
			if (formatValue) return formatValue(value);
			return formatNumber(value, locale);
		},
		[formatValue, locale],
	);

	// Handle slider changes (both drag and release)
	// Updates local state and notifies parent for real-time filter updates
	const handleSliderChange = React.useCallback(
		(next: [number, number]) => {
			setRange(next);
			const { updatedUserMin, updatedUserMax } = deriveNextUserSelection(next);
			setUserMin(updatedUserMin);
			setUserMax(updatedUserMax);
			onChange?.([updatedUserMin, updatedUserMax]);
		},
		[userMin, userMax, initialFrom, initialTo, onChange],
	);

	return (
		<div key={filter.name ?? filter.display_name ?? index} className="space-y-8 pb-8">
			<RangeSlider
				label={labelText}
				min={min}
				max={max}
				step={step}
				value={range}
				onInput={handleSliderChange}
				onChange={handleSliderChange}
				showMarks={false}
			/>
			<div className="flex justify-between text-caption s-text-subtle">
				<span>{formatRangeValue(min)}</span>
				<span>{formatRangeValue(max)}</span>
			</div>
			<RangeForm
				filter={filter}
				locale={locale}
				min={min}
				max={max}
				minLabel={i18n.t({
					id: "explore.searchInput.filters.range.min.label",
					message: "Minimum value",
				})}
				maxLabel={i18n.t({
					id: "explore.searchInput.filters.range.max.label",
					message: "Maximum value",
				})}
				minValue={userMin}
				maxValue={userMax}
				onChange={(values) => {
					const [newMin, newMax] = values;
					setUserMin(newMin);
					setUserMax(newMax);
					setRange([newMin ?? initialFrom, newMax ?? initialTo]);
					onChange?.(values);
				}}
			/>
		</div>
	);
}
